import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/medical-council — English hub
import { createFileRoute, Link } from "@tanstack/react-router";
import { MEDICAL_COUNCIL_PROFESSIONS } from "@/data/medical-council-professions";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Stethoscope, ShieldCheck, MessageCircle, Phone, BadgeCheck, ArrowRight } from "lucide-react";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/medical-council`;

export const Route = createFileRoute("/en/medical-council/")({
  head: () => {
    const title = "Thai Medical Council & Professional Body Certification — 8 Councils | NYC Legal";
    const desc = "Certificate of Good Standing & verification from Thai Medical Council, Nursing Council, Pharmacy Council. Start with a free review before you file anything.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/medical-council", "en") }, { name: "description", content: ctaDesc(desc, "/en/medical-council", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/medical-council", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/medical-council", "en")},
        { property: "og:url", content: URL }, { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
      ],
      links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/medical-council", "/en/medical-council")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${URL}#service`,
              name: "Thai Professional Council Credential Verification",
              serviceType: "Professional Credential Verification",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              areaServed: { "@type": "Country", name: "Thailand" },
              aggregateRating: aggregateRatingNode(URL),
              inLanguage: "en",
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${URL}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Medical Council Certification", item: URL },
              ],
            }),
          },
          faqPageSchema([
            { q: "What is Thai Medical Council certification?", a: "The Thai Medical Council (TMC) issues a Certificate of Good Standing and Verification of Medical Qualification confirming that a physician is legally registered to practise medicine in Thailand. It is required by ECFMG (USA), GMC (UK), AHPRA (Australia), MCC (Canada) and other foreign regulators." },
            { q: "How much does the full package cost?", a: "From THB 5,500–8,500 per professional bundle, including MoJ-certified English translation of the qualification certificate + Notary Public attestation by a court-licensed attorney (License No. NS-2547/xxx). Council fees range THB 200–1,500 (TMC THB 1,000 Good Standing, TNMC THB 500), MFA legalisation THB 800–1,000/document and destination embassy attestation THB 1,400–2,500 — all quoted upfront with no hidden charges." },
            { q: "How long does it take?", a: "Typical turnaround is 7–15 business days depending on the council (TMC 10–15, TNMC 7–14). With MFA + embassy attestation, total is approximately 15–25 business days." },
            { q: "Which Thai councils does NYC handle?", a: "Thai Medical Council (TMC), Thailand Nursing & Midwifery Council (TNMC), Pharmacy Council of Thailand, Dental Council, Medical Technology Council, Physical Therapy Council, Veterinary Council, and Thai Traditional Medical Council — 8 statutory bodies in total." },
            { q: "Do I need to visit the council in person?", a: "No. NYC operates on power of attorney. You provide a signed authorisation, copy of national ID and professional licence; we handle filing, fee payment, document collection and worldwide courier delivery (DHL/FedEx)." },
            { q: "Can TMC send credentials directly to ECFMG (EPIC)?", a: "Yes. TMC sends Verification of Medical Qualification directly to ECFMG via the EPIC portal. NYC prepares the documentation and liaises with TMC's international affairs unit on your behalf." },
          ]),
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Thai Medical Council certification tooling for 8 statutory councils." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(URL)),
      ],
    };
  },
  component: Page,
});

function Page() {
  return (
    <main className="min-h-screen bg-white">
      <AnswerBoxTop cluster="medical-council" locale="en" />
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question="How does NYC handle Thai Medical Council certification?"
          answer={`NYC files Verification / Good Standing with 8 Thai healthcare councils (Medical, Nursing, Pharmacy, Dental, MedTech, Physio, Veterinary, Thai-Trad) on power of attorney — for ECFMG (EPIC), NCLEX, GMC, AHPRA — including translation, MFA legalisation and DHL/FedEx worldwide delivery.`}
          stats={[
            { label: "Councils", value: "8" },
            { label: "Direct-to-ECFMG", value: "Yes" },
            { label: "Worldwide", value: "DHL/FedEx" },
            { label: "Filing", value: "On PoA" },
          ]}
          sourceUrl={URL}
          lang="en"
        />
      </div>
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Medical Council Certification</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stethoscope className="h-3.5 w-3.5" /> Professional Credential Verification — Thailand
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Thai Medical Council & Professional Body Certification
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            End-to-end credential verification for Thai healthcare professionals seeking to register or practise abroad —
            we obtain the Certificate of Good Standing from the relevant council, provide certified English translation
            (MoJ / NAATI), Notary Public attestation, MFA legalisation and destination-embassy attestation.
            Over 2,400 cases delivered for doctors, nurses, pharmacists, dentists and allied health professionals since 2013.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Councils", v: "8" }, { k: "From", v: "THB 5,500" },
              { k: "TAT", v: "7–15 days" }, { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable Stat
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) provides credential verification from 8 Thai professional councils —
          Medical, Nursing, Pharmacy, Dental, Medical Technology, Physical Therapy, Veterinary and Thai Traditional Medicine.
          Starting from THB 5,500, completed in 7–15 business days, rated 4.9/5 across 2,147 reviews. Covers ECFMG (USA),
          GMC (UK), AHPRA (Australia), MCC (Canada), and 20+ destination authorities worldwide.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Choose a Professional Council</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MEDICAL_COUNCIL_PROFESSIONS.map((p) => (
              <Link key={p.slug} to="/en/medical-council/$profession" params={{ profession: p.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{p.councilEn}</div>
                  <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                </div>
                <h3 className="mt-2 font-display text-lg font-extrabold text-navy-deep group-hover:text-amber-700">{p.nameEn}</h3>
                <div className="mt-3 flex items-center gap-3 text-[12px] text-slate-700">
                  <span className="font-semibold">From THB {p.priceFromTh.toLocaleString()}</span>
                  <span className="text-slate-400">·</span>
                  <span>{p.turnaround.replace("วันทำการ", "business days")}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.destinations.slice(0, 4).map((d) => (
                    <span key={d.code} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{d.nameEn}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Standard Workflow</div>
          <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">5 Steps to Foreign Registration</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { n: 1, t: "Council Filing", d: "We file your request with the relevant Thai council under power of attorney" },
              { n: 2, t: "English Translation", d: "Certified by MoJ-registered translator or NAATI (Australia)" },
              { n: 3, t: "Notary Public", d: "Notarial Services Attorney attests the translation" },
              { n: 4, t: "MFA Legalisation", d: "Department of Consular Affairs, Ministry of Foreign Affairs" },
              { n: 5, t: "Embassy Attestation", d: "Destination embassy in Bangkok, then DHL/FedEx worldwide" },
            ].map((s) => (
              <li key={s.n} className="rounded-xl border border-amber-200 bg-white p-4">
                <div className="text-2xl font-extrabold text-amber-600">{s.n}</div>
                <div className="mt-1 font-display font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] leading-relaxed text-slate-600">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-14 rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-700"><ShieldCheck className="h-3 w-3" /> Authority Sources</div>
          <h2 className="mt-1 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">Official References</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-[13px] text-slate-700">
            {MEDICAL_COUNCIL_PROFESSIONS.map((p) => (
              <li key={p.slug}>
                <a href={p.councilUrl} target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">
                  {p.councilEn}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
